// Any image (SVG included) becomes a small PNG in the browser before upload:
// the server only keeps real PNGs up to 512 px (ProductLogo, SponsorshipLogo).
// A wide image (16:9-ish) becomes a 512×288 wordmark, anything else a 256×256
// square; `square: true` forces the square.
// Images over this are not decoded at all: a 200-megapixel photo would take
// the tab down before it was resized. The caller shows Error('huge').
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024

export async function toLogoPng(file: File, { square = false } = {}): Promise<{ png: Blob; wide: boolean }> {
  if (file.size > MAX_SOURCE_BYTES) throw new Error('huge')
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    const w = img.naturalWidth || 256
    const h = img.naturalHeight || 256
    const wide = !square && w / h >= 1.4
    const canvas = document.createElement('canvas')
    canvas.width = wide ? 512 : 256
    canvas.height = wide ? 288 : 256
    const scale = Math.min(canvas.width / w, canvas.height / h)
    const dw = w * scale
    const dh = h * scale
    canvas.getContext('2d')!.drawImage(img, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh)
    const png = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('png'))), 'image/png'))
    return { png, wide }
  } finally {
    URL.revokeObjectURL(url)
  }
}

// Put the converted PNG back into the file input, so the form uploads that.
export function replaceUpload(input: HTMLInputElement, png: Blob, name = 'logo.logo.png'): void {
  const transfer = new DataTransfer()
  transfer.items.add(new File([png], name, { type: 'image/png' }))
  input.files = transfer.files
}

// A screenshot for the garage wall: cover-fitted to 1280×720 and encoded as a
// JPEG (the server keeps real 16:9 JPEGs up to 1600 px / 600 KB).
export async function toScreenshotJpeg(file: File): Promise<Blob> {
  if (file.size > MAX_SOURCE_BYTES) throw new Error('huge')
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    const canvas = document.createElement('canvas')
    canvas.width = 1280
    canvas.height = 720
    const scale = Math.max(canvas.width / (img.naturalWidth || 1280), canvas.height / (img.naturalHeight || 720))
    const dw = (img.naturalWidth || 1280) * scale
    const dh = (img.naturalHeight || 720) * scale
    const x = canvas.getContext('2d')!
    x.fillStyle = '#14161A'
    x.fillRect(0, 0, canvas.width, canvas.height)
    x.drawImage(img, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh)
    let quality = 0.85
    let jpeg = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg'))), 'image/jpeg', quality))
    while (jpeg.size > 580_000 && quality > 0.4) {
      quality -= 0.1
      jpeg = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg'))), 'image/jpeg', quality))
    }
    return jpeg
  } finally {
    URL.revokeObjectURL(url)
  }
}

// A sponsor's sign artwork: the image must have the sign's proportion (within
// 5%, else this throws Error('ratio')); it is then fitted to width × height
// and encoded as a JPEG under 580 KB (SponsorshipArtwork keeps up to 600 KB).
export async function toArtworkJpeg(file: File, width: number, height: number): Promise<Blob> {
  if (file.size > MAX_SOURCE_BYTES) throw new Error('huge')
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    const w = img.naturalWidth || 1
    const h = img.naturalHeight || 1
    if (Math.abs(w / h / (width / height) - 1) > 0.05) throw new Error('ratio')
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const scale = Math.max(width / w, height / h)
    const x = canvas.getContext('2d')!
    x.fillStyle = '#14161A'
    x.fillRect(0, 0, width, height)
    x.drawImage(img, (width - w * scale) / 2, (height - h * scale) / 2, w * scale, h * scale)
    let quality = 0.88
    let jpeg = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg'))), 'image/jpeg', quality))
    while (jpeg.size > 580_000 && quality > 0.4) {
      quality -= 0.1
      jpeg = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg'))), 'image/jpeg', quality))
    }
    return jpeg
  } finally {
    URL.revokeObjectURL(url)
  }
}
